/* Invoices — sales invoices grouped by status (v1/sales).
 *   INITIATED (Ready to bill) — posted bill, awaiting issue / print.
 *   ISSUED                    — finalised + sent to customer.
 *   CANCELLED                 — voided. */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import supplyService from "../../Services/supplyService";
import { useWarehouse } from "../Layout/WarehouseContext";
import { isPharmacyHead } from "../../Services/warehouseContext";
import { cbFmtINR, cbFmtNum } from "./supplyData";
import { buildInvoiceFromSales, invoiceToPdfBlob, openPrintWindow } from "./printInvoice";
import {
  Button,
  Pill,
  Input,
  Select,
  Table,
  Tr,
  Td,
  SectionHeader,
  ToolbarBar,
  DateRangeFilter,
  TabBar,
  Eyebrow,
  LoadingState,
  ErrorState,
  InlineAlert,
  IconDownload,
  IconX,
  IconCheck,
  IconChevRight,
  IconPlus,
} from "./supplyKit";

const pad2 = (n) => String(n).padStart(2, "0");
const toIsoDate = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

/* Last 7 days inclusive (today + the preceding 6). */
const defaultRange = () => {
  const today = new Date();
  const start = new Date();
  start.setDate(today.getDate() - 6);
  return { fromDate: toIsoDate(start), toDate: toIsoDate(today) };
};

/* How many rows a search pulls in one request before matching client-side.
   Big enough that an operator searching a normal window finds their invoice,
   small enough to stay a single, quick request. */
const SEARCH_SCAN = 200;

const STATUS_LABELS = {
  INITIATED: "Ready to bill",
  ISSUED: "Issued",
  CANCELLED: "Cancelled",
};

const statusPillVariant = (status) => {
  if (status === "ISSUED") return "green";
  if (status === "CANCELLED") return "red";
  return "amber";
};

const Billing = () => {
  const navigate = useNavigate();
  const initial = defaultRange();
  const [tab, setTab] = useState("INITIATED");
  const [fromDate, setFromDate] = useState(initial.fromDate);
  const [toDate, setToDate] = useState(initial.toDate);
  const [search, setSearch] = useState("");

  /* `loading` is the initial blocking load (no data yet). `refetching` is the
     softer state used when filters change — we keep the previous rows on
     screen and just show an inline indicator, so the table doesn't flash. */
  const [loading, setLoading] = useState(true);
  const [refetching, setRefetching] = useState(false);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [invoices, setInvoices] = useState([]);
  const [selected, setSelected] = useState(null);
  /* Server-side pagination: the active tab's status is a query param, so one
     request fetches exactly the rows on screen. */
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  /* Totals for the active tab, straight off the page response. */
  const [totalElements, setTotalElements] = useState(0);
  const [serverPages, setServerPages] = useState(0);
  /* Tab badges — counted per status by their own light requests, so they
     describe the whole window rather than the page in view. */
  const [counts, setCounts] = useState({ INITIATED: 0, ISSUED: 0, CANCELLED: 0 });
  /* A pharmacy head still gets the Warehouse column — useful confirmation on each
     row — but no longer a picker: the store comes from the app-wide selection in
     the topbar, and resolving an unset one is the provider's job. */
  const isAdmin = isPharmacyHead();
  const { code: wh } = useWarehouse();

  /* Text search is client-side — v1/sales takes no search param. Matching only
     the page in view would make a search for an invoice on page 7 come back
     empty, so a typed term switches the request to a single wider slice of the
     active tab (the newest SEARCH_SCAN rows) and matches inside that. */
  const term = search.trim().toLowerCase();
  const searching = term.length > 0;
  const serverPage = searching ? 0 : page;
  const serverSize = searching ? SEARCH_SCAN : pageSize;

  useEffect(() => {
    /* An admin with no warehouse selected means "all", so only a regular user
       waits for one to resolve. */
    if (!wh && !isAdmin) return undefined;
    let alive = true;
    setError(null);
    setRefetching(true);
    supplyService
      .listSalesInvoices({
        warehouseId: wh || undefined,
        fromDate,
        toDate,
        /* The tab is the status filter, so a page holds only rows for the tab
           being viewed — no client-side bucketing, and paging can't hide a
           ready-to-bill invoice behind a page of issued ones. */
        status: tab,
        page: serverPage,
        size: serverSize,
      })
      .then((res) => {
        if (!alive) return;
        setInvoices(res.content || []);
        setTotalElements(res.totalElements || 0);
        setServerPages(res.totalPages || 0);
        setLoading(false);
        setRefetching(false);
      })
      .catch((e) => {
        if (!alive) return;
        setError(e?.response?.data?.message || e?.message || "Could not load invoices.");
        setLoading(false);
        setRefetching(false);
      });
    return () => {
      alive = false;
    };
  }, [wh, isAdmin, fromDate, toDate, tab, serverPage, serverSize, reloadKey]);

  /* Tab badges. Separate from the page request so each count covers the whole
     window, and so paging through one tab doesn't re-count the others. */
  useEffect(() => {
    if (!wh && !isAdmin) return undefined;
    let alive = true;
    supplyService
      .countSalesInvoicesByStatus({ warehouseId: wh || undefined, fromDate, toDate })
      .then((c) => alive && setCounts(c))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [wh, isAdmin, fromDate, toDate, reloadKey]);

  const matches = searching
    ? invoices.filter((inv) => {
        const hay = `${inv.number} ${inv.customerName} ${inv.customerPhone || ""} ${inv.orderId || ""}`.toLowerCase();
        return hay.includes(term);
      })
    : invoices;

  /* Two paging modes: the server pages the plain list (rows arrive ready to
     render), while a search pages the matches found inside the scanned slice. */
  const totalRows = searching ? matches.length : totalElements;
  const totalPages = searching
    ? Math.max(1, Math.ceil(matches.length / pageSize))
    : Math.max(1, serverPages || 1);
  const safePage = Math.min(page, totalPages - 1);
  const pagedRows = searching
    ? matches.slice(safePage * pageSize, safePage * pageSize + pageSize)
    : invoices;
  const startRow = totalRows === 0 ? 0 : safePage * pageSize + 1;
  const endRow = searching
    ? Math.min(totalRows, safePage * pageSize + pageSize)
    : safePage * pageSize + pagedRows.length;
  /* A search only sees the slice it scanned, so say when there's more behind it
     rather than implying "no such invoice". */
  const searchScanCapped = searching && totalElements > SEARCH_SCAN;

  /* Back to the first page whenever the tab or a filter changes the row set.
     The open detail aside closes with it — its invoice is no longer on screen. */
  useEffect(() => {
    setPage(0);
    setSelected(null);
  }, [tab, searching, wh, fromDate, toDate, pageSize]);

  const selectedInv = invoices.find((i) => i.id === selected) || null;

  const onResetFilters = () => {
    const r = defaultRange();
    setFromDate(r.fromDate);
    setToDate(r.toDate);
    setSearch("");
    setPage(0);
  };

  return (
    <div className="cb-fade-in" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <SectionHeader
        title="Invoices"
        sub="Sales invoices grouped by status · default window is the last 7 days"
        actions={
          <>
            <Button icon={<IconDownload size={14} />}>Export</Button>
            <Button
              variant="primary"
              icon={<IconPlus size={14} />}
              onClick={() => navigate("/salesBillEntry")}
            >
              Manual invoice
            </Button>
          </>
        }
      />

      <TabBar
        value={tab}
        onChange={(v) => {
          setTab(v);
          setSelected(null);
        }}
        tabs={[
          { id: "INITIATED", label: STATUS_LABELS.INITIATED, count: counts.INITIATED, alert: "amber" },
          { id: "ISSUED", label: STATUS_LABELS.ISSUED, count: counts.ISSUED },
          { id: "CANCELLED", label: STATUS_LABELS.CANCELLED, count: counts.CANCELLED, alert: "red" },
        ]}
      />

      <ToolbarBar>
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Invoice / customer / phone / order…"
          style={{ width: 260, maxWidth: "100%" }}
        />
        <DateRangeFilter
          from={fromDate}
          to={toDate}
          onFromChange={setFromDate}
          onToChange={setToDate}
        />
        {/* No sort control: v1/sales fixes its own newest-invoice-first order and
            drops any sort the client asks for, so the direction can't be offered
            here without the API honouring it. */}
        <Button onClick={onResetFilters}>Reset</Button>
        <div
          style={{
            marginLeft: "auto",
            fontSize: 11,
            color: "var(--fg-muted)",
            fontFamily: "var(--font-mono)",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          {refetching && !loading && <span>Refreshing…</span>}
          <span>
            {searching
              ? `${totalRows} matching · ${counts[tab]} ${STATUS_LABELS[tab].toLowerCase()}`
              : `${counts[tab]} ${STATUS_LABELS[tab].toLowerCase()}`}
          </span>
        </div>
      </ToolbarBar>

      {searchScanCapped && (
        <InlineAlert tone="amber">
          Searching the newest {SEARCH_SCAN} of {counts[tab]}{" "}
          {STATUS_LABELS[tab].toLowerCase()} invoices in this window. Narrow the date range if
          you don't find the invoice you're after.
        </InlineAlert>
      )}

      {loading ? (
        <LoadingState label="Loading invoices…" />
      ) : error ? (
        <ErrorState message={error} onRetry={() => setReloadKey((k) => k + 1)} />
      ) : (
        <div
          className={selectedInv ? "grid-cols-1 lg:grid-cols-[1fr_440px]" : "grid-cols-1"}
          style={{
            display: "grid",
            gap: 12,
            alignItems: "start",
          }}
        >
          <div style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 8 }}>
          <Table
            columns={[
              { label: "Invoice #" },
              { label: "Date" },
              ...(isAdmin ? [{ label: "Warehouse" }] : []),
              { label: "Customer" },
              { label: "Items", align: "right" },
              { label: "Qty", align: "right" },
              { label: "Net", align: "right" },
              { label: "Status" },
              { label: "", width: 24 },
            ]}
          >
            {pagedRows.map((inv) => {
              const itemCount = inv.items.length;
              const units = inv.items.reduce((s, it) => s + (Number(it.quantity) || 0), 0);
              return (
                <Tr
                  key={inv.id}
                  selected={selected === inv.id}
                  onClick={() => setSelected(selected === inv.id ? null : inv.id)}
                >
                  <Td mono style={{ fontWeight: 600 }}>
                    {inv.number}
                  </Td>
                  <Td mono>{inv.invoiceDate}</Td>
                  {isAdmin && <Td>{inv.warehouseName || inv.warehouseId || "—"}</Td>}
                  <Td>
                    <div style={{ fontWeight: 600, color: "var(--fg-strong)" }}>
                      {inv.customerName}
                    </div>
                    {inv.customerPhone && (
                      <div
                        style={{
                          fontSize: 10,
                          color: "var(--fg-muted)",
                          fontFamily: "var(--font-mono)",
                          marginTop: 2,
                        }}
                      >
                        {inv.customerPhone}
                      </div>
                    )}
                  </Td>
                  <Td mono right>
                    {itemCount}
                  </Td>
                  <Td mono right>
                    {cbFmtNum(units)}
                  </Td>
                  <Td
                    mono
                    right
                    style={{ fontWeight: 700, fontSize: 13, color: "var(--fg-strong)" }}
                  >
                    {cbFmtINR(inv.netAmount)}
                  </Td>
                  <Td>
                    <Pill variant={statusPillVariant(inv.status)} dot>
                      {inv.status}
                    </Pill>
                  </Td>
                  <Td>
                    <IconChevRight size={12} style={{ color: "var(--fg-muted)" }} />
                  </Td>
                </Tr>
              );
            })}
            {pagedRows.length === 0 && (
              <Tr>
                <Td
                  colSpan={isAdmin ? 9 : 8}
                  style={{ textAlign: "center", padding: 28, color: "var(--fg-muted)", fontSize: 12 }}
                >
                  {searching
                    ? `No ${STATUS_LABELS[tab].toLowerCase()} invoices match “${search.trim()}”.`
                    : `No ${STATUS_LABELS[tab].toLowerCase()} invoices in the selected window.`}
                </Td>
              </Tr>
            )}
          </Table>

          <ToolbarBar>
            <Select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              aria-label="Rows per page"
            >
              {[25, 50, 100, 200].map((n) => (
                <option key={n} value={n}>
                  {n} / page
                </option>
              ))}
            </Select>
            <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontSize: 11, color: "var(--fg-muted)", fontFamily: "var(--font-mono)" }}>
                {startRow}–{endRow} of {cbFmtNum(totalRows)} · page {safePage + 1} of {totalPages}
              </span>
              <Button
                size="sm"
                disabled={safePage <= 0}
                onClick={() => setPage(Math.max(0, safePage - 1))}
              >
                ← Prev
              </Button>
              <Button
                size="sm"
                disabled={safePage + 1 >= totalPages}
                onClick={() => setPage(safePage + 1)}
              >
                Next →
              </Button>
            </div>
          </ToolbarBar>
          </div>

          {selectedInv && (
            <InvoiceDetail
              invoice={selectedInv}
              onClose={() => setSelected(null)}
              onUploaded={() => setReloadKey((k) => k + 1)}
            />
          )}
        </div>
      )}

    </div>
  );
};

/* ---------------- Detail aside ---------------- */
const InvoiceDetail = ({ invoice, onClose, onUploaded }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState(null);
  const [downloading, setDownloading] = useState(false);
  /* Payment transaction id (refTxnId) — not on the sales row, so it's looked
     up from the order-list by orderId. `undefined` = not yet resolved,
     `null` = resolved but none found. */
  const [txnId, setTxnId] = useState(undefined);

  /* Reset error state + transaction id when the focused invoice changes, then
     look the transaction id up from the order-list for display and reuse on
     bill creation. Best-effort — a miss just hides the row. */
  useEffect(() => {
    setUploadErr(null);
    setTxnId(undefined);
    /* Prefer the value the backend persisted on the sales row; only hit the
       order-list when it's missing. */
    if (invoice.transactionId) {
      setTxnId(invoice.transactionId);
      return undefined;
    }
    if (!invoice.orderId) {
      setTxnId(null);
      return undefined;
    }
    let alive = true;
    supplyService
      .getOrderTransactionId(invoice.orderId)
      .then((id) => {
        if (alive) setTxnId(id ?? null);
      })
      .catch(() => {
        if (alive) setTxnId(null);
      });
    return () => {
      alive = false;
    };
  }, [invoice.id, invoice.orderId]);

  const canCreateBill = invoice.status === "INITIATED";

  /* Render the saved invoice to a PDF blob (html2pdf), upload it to
     v1/sales/{id}/document, then open the print window so the operator can
     print a physical copy if needed. */
  const handleCreateBill = () => {
    setUploading(true);
    setUploadErr(null);
    /* The sales row doesn't carry the payment transaction id. It's usually
       already resolved by the effect above; fall back to a fresh lookup if the
       panel opened and printed before that settled. */
    const resolveTxn =
      txnId !== undefined
        ? Promise.resolve(txnId)
        : supplyService.getOrderTransactionId(invoice.orderId);
    resolveTxn
      .then((id) => {
        const built = buildInvoiceFromSales(invoice, { txnId: id });
        if (!built) {
          setUploading(false);
          setUploadErr("This invoice has no line items to render.");
          return;
        }
        return invoiceToPdfBlob(built)
          .then((file) => supplyService.uploadSalesDocument(invoice.id, file))
          .then(() => {
            openPrintWindow(built);
            setUploading(false);
            onUploaded && onUploaded();
          });
      })
      .catch((e) => {
        setUploading(false);
        setUploadErr(
          e?.response?.data?.message || e?.message || "Could not upload the document."
        );
      });
  };

  /* Download the stored invoice PDF through the backend API
     (GET v1/sales/{id}/document) rather than opening the raw storage URL —
     the request carries the auth header and the file stays behind the API.
     The blob is opened in a new tab (browsers render/download a PDF blob URL). */
  const handleDownload = () => {
    setDownloading(true);
    setUploadErr(null);
    supplyService
      .downloadSalesDocument(invoice.id)
      .then((blob) => {
        const url = window.URL.createObjectURL(
          blob instanceof Blob ? blob : new Blob([blob], { type: "application/pdf" })
        );
        const a = document.createElement("a");
        a.href = url;
        a.download = `Invoice ${invoice.number || invoice.id}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        /* Revoke after a tick so the navigation/download has grabbed the URL. */
        setTimeout(() => window.URL.revokeObjectURL(url), 1000);
        setDownloading(false);
      })
      .catch((e) => {
        setDownloading(false);
        setUploadErr(
          e?.response?.data?.message || e?.message || "Could not download the document."
        );
      });
  };

  return (
    <aside style={detailAside} className="min-w-0 lg:sticky lg:top-3 lg:max-h-[calc(100vh_-_130px)]">
      <div style={detailHeader}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, whiteSpace: "nowrap" }}>
            <span
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 13,
                fontWeight: 700,
                color: "var(--fg-strong)",
              }}
            >
              {invoice.number}
            </span>
            <Pill variant={statusPillVariant(invoice.status)} dot>
              {invoice.status}
            </Pill>
          </div>
          <div style={{ fontSize: 11, color: "var(--fg-muted)", marginTop: 4 }}>
            {invoice.invoiceDate} · {invoice.warehouseName}
            {invoice.orderId && <> · Order {invoice.orderId}</>}
          </div>
          {txnId && (
            <div
              style={{
                fontSize: 10,
                color: "var(--fg-muted)",
                fontFamily: "var(--font-mono)",
                marginTop: 3,
                wordBreak: "break-all",
              }}
            >
              Txn {txnId}
            </div>
          )}
        </div>
        <button
          onClick={onClose}
          style={{
            width: 24,
            height: 24,
            border: 0,
            background: "transparent",
            cursor: "pointer",
            color: "var(--fg-muted)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <IconX size={14} />
        </button>
      </div>

      <div
        style={{
          flex: 1,
          overflow: "auto",
          padding: "12px 16px",
          display: "flex",
          flexDirection: "column",
          gap: 14,
        }}
      >
        <section>
          <Eyebrow style={{ display: "block", marginBottom: 6 }}>Bill to</Eyebrow>
          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--fg-strong)" }}>
            {invoice.customerName}
          </div>
          {invoice.customerPhone && (
            <div
              style={{
                fontSize: 11,
                color: "var(--fg-muted)",
                fontFamily: "var(--font-mono)",
                marginTop: 2,
              }}
            >
              {invoice.customerPhone}
            </div>
          )}
          {invoice.doctorName && (
            <div style={{ fontSize: 11, color: "var(--fg-muted)", marginTop: 4 }}>
              Dr. {invoice.doctorName}
            </div>
          )}
        </section>

        <section>
          <Eyebrow style={{ display: "block", marginBottom: 6 }}>Line items</Eyebrow>
          <div
            style={{
              border: "1px solid var(--border-default)",
              borderRadius: 4,
              overflowX: "auto",
            }}
          >
            <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 11 }}>
              <thead>
                <tr style={{ background: "var(--bg-subtle)" }}>
                  <th style={detailTh}>Medicine</th>
                  <th style={{ ...detailTh, textAlign: "right", width: 40 }}>Qty</th>
                  <th style={{ ...detailTh, textAlign: "right", width: 64 }}>Rate</th>
                  <th style={{ ...detailTh, textAlign: "right", width: 70 }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {invoice.items.map((it) => (
                  <tr key={it.id}>
                    <td style={detailTd}>
                      <div style={{ fontWeight: 600, color: "var(--fg-strong)" }}>
                        {it.medicineName}
                      </div>
                      <div
                        style={{
                          fontFamily: "var(--font-mono)",
                          fontSize: 10,
                          color: "var(--fg-muted)",
                          marginTop: 2,
                        }}
                      >
                        {it.medicineCode} · B {it.batchNo} · Exp {it.expiryDate}
                      </div>
                    </td>
                    <td
                      style={{
                        ...detailTd,
                        textAlign: "right",
                        fontFamily: "var(--font-mono)",
                      }}
                    >
                      {it.quantity}
                    </td>
                    <td
                      style={{
                        ...detailTd,
                        textAlign: "right",
                        fontFamily: "var(--font-mono)",
                      }}
                    >
                      {cbFmtINR(it.sellingPrice)}
                    </td>
                    <td
                      style={{
                        ...detailTd,
                        textAlign: "right",
                        fontFamily: "var(--font-mono)",
                        fontWeight: 600,
                      }}
                    >
                      {cbFmtINR(it.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section
          style={{
            padding: "10px 12px",
            background: "var(--bg-subtle)",
            borderRadius: 4,
            display: "flex",
            flexDirection: "column",
            gap: 5,
            fontSize: 12,
          }}
        >
          <SumLine label="Subtotal" value={cbFmtINR(invoice.totalAmount)} />
          <SumLine label="Discount" value={cbFmtINR(invoice.discountAmount)} muted />
          {/* CureCoin is a separate header discount (not folded into discountAmount),
              so show it as its own line or Net won't foot: Net = Subtotal − Discount
              − CureCoin. Hidden when nothing was redeemed. */}
          {Number(invoice.cureCoinDiscount) > 0 && (
            <SumLine label="CureCoin" value={cbFmtINR(invoice.cureCoinDiscount)} muted />
          )}
          <SumLine label="GST" value={cbFmtINR(invoice.gstAmount)} muted />
          <div style={{ height: 1, background: "var(--border-subtle)", margin: "2px 0" }} />
          <SumLine label="Net" value={cbFmtINR(invoice.netAmount)} bold size={14} />
        </section>

        {invoice.status === "CANCELLED" && invoice.cancelReason && (
          <section
            style={{
              padding: "10px 12px",
              background: "var(--red-50)",
              borderRadius: 4,
              fontSize: 11,
              color: "var(--red-700)",
            }}
          >
            <Eyebrow style={{ display: "block", marginBottom: 4, color: "var(--red-700)" }}>
              Cancelled
            </Eyebrow>
            <div>{invoice.cancelReason}</div>
            {invoice.cancelledBy && (
              <div style={{ marginTop: 4, fontFamily: "var(--font-mono)" }}>
                by {invoice.cancelledBy}
                {invoice.cancelledAt ? ` · ${invoice.cancelledAt}` : ""}
              </div>
            )}
          </section>
        )}
      </div>

      {uploadErr && (
        <div style={{ padding: "10px 16px 0" }}>
          <InlineAlert>{uploadErr}</InlineAlert>
        </div>
      )}

      <div
        style={{
          padding: "10px 16px",
          borderTop: "1px solid var(--border-default)",
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}
      >
        {invoice.documentUrl && (
          <Button
            style={{ justifyContent: "center" }}
            icon={<IconDownload size={14} />}
            onClick={handleDownload}
            disabled={downloading}
          >
            {downloading ? "Downloading…" : "Download PDF"}
          </Button>
        )}

        {canCreateBill && (
          <Button
            variant="primary"
            icon={<IconCheck size={14} />}
            style={{ justifyContent: "center" }}
            onClick={handleCreateBill}
            disabled={uploading}
          >
            {uploading ? "Generating…" : "Create bill"}
          </Button>
        )}
      </div>
    </aside>
  );
};

const detailTh = {
  padding: "5px 8px",
  fontSize: 9,
  fontWeight: 600,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: "var(--fg-muted)",
  textAlign: "left",
  borderBottom: "1px solid var(--border-default)",
};
const detailTd = {
  padding: "7px 8px",
  borderBottom: "1px solid var(--border-subtle)",
  verticalAlign: "top",
};
const detailAside = {
  border: "1px solid var(--border-default)",
  borderRadius: 6,
  background: "var(--bg-panel)",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};
const detailHeader = {
  padding: "12px 16px",
  borderBottom: "1px solid var(--border-default)",
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: 8,
};

const SumLine = ({ label, value, muted, bold, size }) => (
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      gap: 8,
      fontWeight: bold ? 700 : 500,
      color: bold ? "var(--fg-strong)" : muted ? "var(--fg-muted)" : "var(--fg-default)",
      fontSize: size || 12,
    }}
  >
    <span style={{ whiteSpace: "nowrap" }}>{label}</span>
    <span style={{ fontFamily: "var(--font-mono)", fontVariantNumeric: "tabular-nums" }}>
      {value}
    </span>
  </div>
);

export default Billing;
